import { useState, useCallback, useContext, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import {
  addIndication,
  addStudyData,
  copyStaleIndication,
  deleteIndication,
  deleteStudy,
  duplicateIndication,
  getAttributes,
  getEndpointValues,
  updateIndication,
  updateIndicationStale,
  updateStudyData
} from '../../../api/submission_details';
import Store from '../../../store';
import { HttpResponse, STUDY_KEY_TITLE } from '../constant';

import Actions from '../../../store/actions';
import { SharedStudyConfirmationPayload, SourceSubmission } from '../types';

type PendingSharedStudyEdit = {
  indicationId: number;
  studyData: any;
  payload: any;
  confirmation: SharedStudyConfirmationPayload;
};

const SOURCE_ONLY_ERROR = 'Edits are only allowed on the source submission';

const getSourceSubmissionKey = (sourceSubmission: SourceSubmission | null): string => {
  if (!sourceSubmission) {
    return 'the source submission';
  }

  return `${sourceSubmission.applno}-${sourceSubmission.submission_type}-${sourceSubmission.submission_number}`;
};

const getSourceOnlyMessage = (sourceSubmission: SourceSubmission | null) => {
  const documentIds = sourceSubmission?.document_ids?.length
    ? ` (document IDs: ${sourceSubmission.document_ids.join(', ')})`
    : '';
  return `${SOURCE_ONLY_ERROR}. Please edit ${getSourceSubmissionKey(
    sourceSubmission
  )}${documentIds}.`;
};

const isSourceOnlyResponse = (response: any): boolean => {
  const status = response?.data?.Status ?? response?.status;
  const message = response?.data?.Error ?? response?.data?.message ?? '';
  return status === 400 && String(message).includes(SOURCE_ONLY_ERROR);
};

const isSharedStudyConfirmationResponse = (
  response: any
): response is { data: { Success: SharedStudyConfirmationPayload; Status: 409 } } => {
  return (
    (response?.data?.Status ?? response?.status) === 409 &&
    response?.data?.Success?.requires_confirmation === true
  );
};

const buildStudyPayload = (studyData: any, user: any) => ({
  nct_id: studyData.nct_id,
  identifier: studyData.identifier,
  center: studyData.center,
  control: studyData.control,
  randomized: studyData.randomized,
  blinding: studyData.blinding,
  combined_study_count: studyData.combined_study_count,
  has_combined_study_data: studyData.has_combined_study_data,
  is_label_diff_from_clinical_posting: studyData.is_label_diff_from_clinical_posting,
  label_diff_from_clinical_posting_notes: studyData.label_diff_from_clinical_posting_notes,
  study_updated_status: studyData.study_updated_status,
  attributes: studyData.attributes,
  ...user
});

const useIndicationAPIs = (
  applno: string,
  submissionType: string,
  submissionNumber: any,
  apiIndications: any,
  center: string,
  documentId: number,
  sourceSubmission: SourceSubmission | null = null
) => {
  const { dispatch } = useContext<any>(Store);
  const [indications, setIndications] = useState<Array<any>>([]);
  const [attributes, setAttributes] = useState<Array<any>>([]);
  const [endpointOptions, setEndpointOptions] = useState<Array<any>>([]);
  const [pendingSharedStudyEdit, setPendingSharedStudyEdit] =
    useState<PendingSharedStudyEdit | null>(null);
  const [confirmingSharedStudyEdit, setConfirmingSharedStudyEdit] = useState(false);
  const [savingStudy, setSavingStudy] = useState(false);

  const [user, setUser] = useState<any>();

  useEffect(() => {
    (async () => {
      const authUser = await Auth.currentUserInfo();
      setUser({
        curator_name: authUser.attributes['custom:user'],
        curator_email: authUser.attributes.email
      });
    })();
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    getPossibleAttributes();
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    getEndpointOptions();
  }, []);

  useEffect(() => {
    setIndications(
      apiIndications?.map((ind: any) => ({
        ...ind,
        indication: {
          ...ind.indication,
          studies: ind.indication.studies?.map((stud: any) => ({
            ...stud
          }))
        }
      }))
    );
  }, [apiIndications]);

  const alert = useCallback((message: string, type: string) => {
    dispatch({
      type: Actions.SET_ALERT,
      value: {
        status: true,
        message,
        color: type
      }
    });
  }, []);

  const showSourceOnlyAlert = useCallback(() => {
    alert(getSourceOnlyMessage(sourceSubmission), 'error');
  }, [alert, sourceSubmission]);

  const handleSourceOnlyResponse = useCallback(
    (response: any): boolean => {
      if (!isSourceOnlyResponse(response)) {
        return false;
      }

      showSourceOnlyAlert();
      return true;
    },
    [showSourceOnlyAlert]
  );

  const isCopiedSourceIndication = useCallback(
    (indicationWrapper: any): boolean => {
      if (!sourceSubmission || !indicationWrapper || indicationWrapper.newIndication) {
        return false;
      }

      return indicationWrapper.from_prev_submission !== false;
    },
    [sourceSubmission]
  );

  const getEndpointOptions = useCallback(async () => {
    // eslint-disable-next-line no-console
    const response = await getEndpointValues().catch(console.error);
    if (response?.data?.Status === HttpResponse.OK) {
      setEndpointOptions(response.data.Success || []);
    }
  }, []);

  const getPossibleAttributes = useCallback(async () => {
    // eslint-disable-next-line no-console
    const response = await getAttributes().catch(console.error);
    if (response.data?.Status === HttpResponse.OK) {
      setAttributes(response.data.Success);
    } else {
      alert('Failed to get attributes. You will not be able to add studies', 'error');
    }
  }, []);

  const expandIndication = useCallback(
    (id: Number) => {
      const newIndications = [...indications];
      const index = newIndications.findIndex((ind: any) => ind.indication.id === id);
      newIndications[index].expanded = !newIndications[index].expanded;
      setIndications(newIndications);
    },
    [indications]
  );

  const editIndication = useCallback(
    (id: Number) => {
      const newIndications = [...indications];
      const index = newIndications.findIndex((ind: any) => ind.indication.id === id);
      if (isCopiedSourceIndication(newIndications[index])) {
        showSourceOnlyAlert();
        return;
      }

      newIndications[index].editOpen = !newIndications[index].editOpen;
      setIndications(newIndications);
    },
    [indications, isCopiedSourceIndication, showSourceOnlyAlert]
  );

  const saveIndication = useCallback(
    async (id: Number) => {
      const newIndications = [...indications];
      const index = newIndications.findIndex((ind: any) => ind.indication.id === id);
      if (isCopiedSourceIndication(newIndications[index])) {
        showSourceOnlyAlert();
        return;
      }

      if (newIndications[index].newIndication || newIndications[index].dataFromPrevSubmission) {
        try {
          const response = await addIndication({
            text: newIndications[index].indication.text,
            applno,
            submission_type: submissionType,
            submission_number: submissionNumber,
            center,
            document_id: documentId,
            ...user
          });
          if (response.data.Status === HttpResponse.OK) {
            let { studies } = response.data.Success.app_indication.indication;
            if (studies.length < newIndications[index].indication.studies.length) {
              studies = newIndications[index].indication.studies;
            }
            newIndications[index] = {
              ...newIndications[index],
              ...response.data.Success.app_indication,
              ...{ indication: { ...response.data.Success.app_indication.indication, studies } },
              newIndication: false,
              dataFromPrevSubmission: false,
              from_prev_submission: false
            };
            newIndications[index].editOpen = false;
            setIndications(newIndications);
            alert('New Indication added', 'success');
          } else if (!handleSourceOnlyResponse(response)) {
            alert('Failed to add new Indication', 'error');
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);
          alert('Failed to add new Indication', 'error');
        }
      } else {
        // Handle Edit
        const response = await updateIndication(
          applno,
          submissionType,
          submissionNumber,
          newIndications[index].indication.id,
          {
            text: newIndications[index].indication.text,
            ...user
            // eslint-disable-next-line no-console
          }
          // eslint-disable-next-line no-console
        ).catch(console.error);
        if (response.data.Status === HttpResponse.OK) {
          if (typeof response.data.Success?.app_indication !== 'boolean') {
            newIndications[index].indication.id = response.data.Success.app_indication.id;
          }
          newIndications[index].editOpen = false;
          setIndications(newIndications);
          alert('Indication updated successfully', 'success');
        } else if (!handleSourceOnlyResponse(response)) {
          alert('Failed to update indication', 'error');
        }
      }
    },
    [
      indications,
      user,
      applno,
      submissionType,
      submissionNumber,
      isCopiedSourceIndication,
      showSourceOnlyAlert,
      handleSourceOnlyResponse
    ]
  );

  const updateIndicationText = useCallback(
    (id: number, value: string) => {
      const newIndications = [...indications];
      const index = newIndications.findIndex((ind: any) => ind.indication.id === id);
      if (isCopiedSourceIndication(newIndications[index])) {
        showSourceOnlyAlert();
        return;
      }

      newIndications[index].indication.text = value;
      setIndications(newIndications);
    },
    [indications, isCopiedSourceIndication, showSourceOnlyAlert]
  );

  const addEmptyIndication = useCallback(() => {
    if (sourceSubmission) {
      showSourceOnlyAlert();
      return;
    }

    const newIndications = [...indications];
    newIndications.push({
      id: Date.now(),
      newIndication: true,
      indication: {
        id: Date.now(),
        text: '',
        studies: []
      },
      expanded: true,
      editOpen: true
    });
    setIndications(newIndications);
  }, [indications, sourceSubmission, showSourceOnlyAlert]);

  const addEmptyStudy = useCallback(
    (id: number) => {
      const newIndications = [...indications];
      const index = newIndications.findIndex((ind: any) => ind.indication.id === id);
      if (isCopiedSourceIndication(newIndications[index])) {
        showSourceOnlyAlert();
        return;
      }

      // eslint-disable-next-line camelcase
      const attrs = attributes.map(({ attribute_id }: any) => ({
        // eslint-disable-next-line camelcase
        attribute_id,
        attribute_value: null
      }));
      if (attrs.length > 2) {
        attrs.length = 2;
      }
      newIndications[index].indication.studies.push({
        id: Date.now(),
        formOpen: true,
        newStudy: true,
        attributes: attrs,
        ...Object.keys(STUDY_KEY_TITLE).reduce(
          (mergedObj, currentKey) => ({
            ...mergedObj,
            [currentKey]: STUDY_KEY_TITLE[currentKey].default
          }),
          {}
        )
      });
      setIndications(newIndications);
    },
    [attributes, indications, isCopiedSourceIndication, showSourceOnlyAlert]
  );

  const editStudy = useCallback(
    (indicationId: number, studyData: any) => {
      const newIndications = [...indications];
      const index = newIndications.findIndex((ind: any) => ind.indication.id === indicationId);
      if (isCopiedSourceIndication(newIndications[index])) {
        showSourceOnlyAlert();
        return;
      }

      const studyIndex = newIndications[index].indication.studies.findIndex(
        (st: any) => st.id === studyData.id
      );

      newIndications[index].indication.studies[studyIndex] = {
        ...newIndications[index].indication.studies[studyIndex],
        formOpen: true
      };
      setIndications(newIndications);
    },
    [indications, isCopiedSourceIndication, showSourceOnlyAlert]
  );

  const applyStudySaveSuccess = useCallback(
    (indicationId: number, studyData: any, success: any) => {
      setIndications(previous =>
        previous.map((item: any) => {
          if (item.indication.id !== indicationId) {
            return item;
          }

          const nextIndicationId = success?.indication?.id ?? item.indication.id;
          return {
            ...item,
            indication: {
              ...item.indication,
              id: nextIndicationId,
              studies: item.indication.studies.map((study: any) =>
                study.id === studyData.id
                  ? {
                      ...success,
                      newStudy: false,
                      formOpen: false
                    }
                  : study
              )
            }
          };
        })
      );
      getEndpointOptions();
    },
    [getEndpointOptions]
  );

  const saveStudyData = useCallback(
    async (indicationId: number, studyData: any) => {
      const newIndications = [...indications];
      const index = newIndications.findIndex((ind: any) => ind.indication.id === indicationId);
      if (isCopiedSourceIndication(newIndications[index])) {
        showSourceOnlyAlert();
        return;
      }

      const payload = buildStudyPayload(studyData, user);
      setSavingStudy(true);

      try {
        if (studyData.newStudy) {
          try {
            const response = await addStudyData(
              applno,
              submissionType,
              submissionNumber,
              indicationId,
              payload
            );
            if (response.data.Status === HttpResponse.OK) {
              applyStudySaveSuccess(indicationId, studyData, response.data.Success);
              alert('Study data added successfully', 'success');
            } else if (!handleSourceOnlyResponse(response)) {
              alert('Failed to add Study Data', 'error');
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
            alert('Failed to add Study Data', 'error');
          }
        } else {
          const response = await updateStudyData(
            applno,
            submissionType,
            submissionNumber,
            indicationId,
            studyData.id,
            payload
            // eslint-disable-next-line no-console
          ).catch(console.error);
          if (response?.data?.Status === HttpResponse.OK) {
            applyStudySaveSuccess(indicationId, studyData, response.data.Success);
            alert('Study data updated successfully', 'success');
          } else if (isSharedStudyConfirmationResponse(response)) {
            setPendingSharedStudyEdit({
              indicationId,
              studyData,
              payload,
              confirmation: response.data.Success
            });
          } else if (!handleSourceOnlyResponse(response)) {
            alert('Failed to update Study Data', 'error');
          }
        }
      } finally {
        setSavingStudy(false);
      }
    },
    [
      indications,
      user,
      applno,
      submissionType,
      submissionNumber,
      isCopiedSourceIndication,
      showSourceOnlyAlert,
      applyStudySaveSuccess,
      handleSourceOnlyResponse
    ]
  );

  const deleteStudyData = useCallback(
    async (indicationId: number, studyData: any) => {
      const newIndications = [...indications];
      const index = newIndications.findIndex((ind: any) => ind.indication.id === indicationId);
      if (isCopiedSourceIndication(newIndications[index])) {
        showSourceOnlyAlert();
        return;
      }

      const studyIndex = newIndications[index].indication.studies.findIndex(
        (st: any) => st.id === studyData.id
      );
      if (studyData.newStudy) {
        newIndications[index].indication.studies.splice(studyIndex, 1);
        setIndications(newIndications);
      } else {
        // Call delete API
        // eslint-disable-next-line no-console
        const response = await deleteStudy(
          applno,
          submissionType,
          submissionNumber,
          indicationId,
          studyData.id
          // eslint-disable-next-line no-console
        ).catch(console.error);
        if (response?.data?.Status === HttpResponse.OK) {
          if (typeof response.data.Success.deleted !== 'boolean') {
            newIndications[index].indication.id = response.data.Success.deleted.id;
          }
          newIndications[index].indication.studies.splice(studyIndex, 1);
          setIndications(newIndications);
          alert('Study data deleted successfully', 'success');
        } else if (!handleSourceOnlyResponse(response)) {
          // eslint-disable-next-line no-console
          console.error('failed to delete', indicationId, studyData);
          alert('Failed to delete Study data', 'error');
        }
      }
    },
    [
      indications,
      applno,
      submissionType,
      submissionNumber,
      isCopiedSourceIndication,
      showSourceOnlyAlert,
      handleSourceOnlyResponse
    ]
  );

  const updateStaleStatus = useCallback(
    async (indicationId: number) => {
      if (sourceSubmission) {
        showSourceOnlyAlert();
        return;
      }
      try {
        const response = await updateIndicationStale(
          applno,
          submissionType,
          submissionNumber,
          indicationId,
          {
            stale_status: false,
            center
          }
        ).catch(console.error);
        if (response?.data?.Status === HttpResponse.OK) {
          const newIndications = [...indications];
          const index = newIndications.findIndex((ind: any) => ind.indication.id === indicationId);
          newIndications[index] = {
            ...newIndications[index],
            is_stale: false
          };
          setIndications(newIndications);
          alert('Updated Stale Status', 'success');
        } else {
          alert('Failed to update Stale Status', 'error');
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        alert('Failed to update Stale Status', 'error');
      }
    },
    [indications, applno, submissionType, submissionNumber, sourceSubmission, showSourceOnlyAlert]
  );

  const copyStaleIndications = useCallback(
    async (selectedIndicationForComparison: Array<any>) => {
      if (sourceSubmission) {
        showSourceOnlyAlert();
        return;
      }
      try {
        const response = await copyStaleIndication(applno, submissionType, submissionNumber, {
          current_indication_id: selectedIndicationForComparison[1].id,
          parent_indication_id: selectedIndicationForComparison[0].id,
          center
        }).catch(console.error);
        if (response?.data?.Status === HttpResponse.OK) {
          const newIndications = [...indications];
          const index = newIndications.findIndex(
            (ind: any) => ind.indication.id === selectedIndicationForComparison[1].id
          );
          newIndications[index] = {
            ...newIndications[index],
            indication: { ...selectedIndicationForComparison[0] },
            is_stale: false
          };
          setIndications(newIndications);

          alert('Copied indication successfully', 'success');
        } else {
          alert('Failed to copy', 'error');
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        alert('Failed to copy', 'error');
      }
    },
    [indications, applno, submissionType, submissionNumber, sourceSubmission, showSourceOnlyAlert]
  );

  const rejectIdentical = useCallback(
    (indicationId: number) => {
      if (sourceSubmission) {
        showSourceOnlyAlert();
        return;
      }
      const newIndications = [...indications];
      const index = newIndications.findIndex((ind: any) => ind.indication.id === indicationId);
      newIndications[index] = {
        ...newIndications[index],
        indication: {
          ...newIndications[index].indication,
          studies: newIndications[index].indication.studies?.map((stud: any) => ({
            ...stud,
            formOpen: true,
            newStudy: true
          }))
        },
        newIndication: true,
        dataFromPrevSubmission: false,
        editOpen: true
      };

      setIndications(newIndications);
    },
    [indications, sourceSubmission, showSourceOnlyAlert]
  );

  const copyIndicationFromPrev = useCallback(
    async (indicationId: number) => {
      if (sourceSubmission) {
        showSourceOnlyAlert();
        return;
      }
      const newIndications = [...indications];
      const index = newIndications.findIndex((ind: any) => ind.indication.id === indicationId);
      try {
        const response = await duplicateIndication({
          applno,
          indicationId,
          submission_number: submissionNumber,
          submission_type: submissionType,
          center,
          document_id: documentId,
          ...user
        });
        if (response?.data?.Status === HttpResponse.OK) {
          newIndications[index] = {
            ...newIndications[index],
            dataFromPrevSubmission: false
          };
          setIndications(newIndications);
        } else {
          alert('Failed to mark indication identical', 'error');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        alert('Failed to mark indication identical', 'error');
      }
    },
    [
      indications,
      user,
      applno,
      submissionType,
      submissionNumber,
      sourceSubmission,
      showSourceOnlyAlert
    ]
  );

  const deleteIndicationId = useCallback(
    async (indicationId: number) => {
      const newIndications = [...indications];
      const index = newIndications.findIndex((ind: any) => ind.indication.id === indicationId);
      if (isCopiedSourceIndication(newIndications[index])) {
        showSourceOnlyAlert();
        return;
      }

      if (newIndications[index].newIndication || newIndications[index].dataFromPrevSubmission) {
        newIndications.splice(index, 1);
        setIndications(newIndications);
      } else {
        const response = await deleteIndication(
          applno,
          submissionType,
          submissionNumber,
          indicationId
          // eslint-disable-next-line no-console
        ).catch(console.error);
        if (response?.data?.Status === HttpResponse.OK) {
          newIndications.splice(index, 1);
          setIndications(newIndications);
          alert('Indication deleted successfully', 'success');
        } else if (!handleSourceOnlyResponse(response)) {
          // eslint-disable-next-line no-console
          console.error('failed to delete', indicationId);
          alert('Failed to delete Indication', 'error');
        }
      }
    },
    [
      indications,
      applno,
      submissionType,
      submissionNumber,
      isCopiedSourceIndication,
      showSourceOnlyAlert,
      handleSourceOnlyResponse
    ]
  );

  const cancelSharedStudyEdit = useCallback(() => {
    setPendingSharedStudyEdit(null);
  }, []);

  const confirmSharedStudyEdit = useCallback(
    async ({
      applyToAll,
      selectedSourceAppIds
    }: {
      applyToAll: boolean;
      selectedSourceAppIds: number[];
    }) => {
      if (!pendingSharedStudyEdit) {
        return;
      }

      setConfirmingSharedStudyEdit(true);
      try {
        const response = await updateStudyData(
          applno,
          submissionType,
          submissionNumber,
          pendingSharedStudyEdit.indicationId,
          pendingSharedStudyEdit.studyData.id,
          {
            ...pendingSharedStudyEdit.payload,
            confirm_shared_study_edit: true,
            ...(applyToAll
              ? { apply_to_all_source_submissions: true }
              : { selected_source_app_ids: selectedSourceAppIds })
          }
          // eslint-disable-next-line no-console
        ).catch(console.error);

        if (response?.data?.Status === HttpResponse.OK) {
          applyStudySaveSuccess(
            pendingSharedStudyEdit.indicationId,
            pendingSharedStudyEdit.studyData,
            response.data.Success
          );
          setPendingSharedStudyEdit(null);
          alert('Study data updated successfully', 'success');
        } else if (!handleSourceOnlyResponse(response)) {
          alert('Failed to update Study Data', 'error');
        }
      } finally {
        setConfirmingSharedStudyEdit(false);
      }
    },
    [
      pendingSharedStudyEdit,
      applno,
      submissionType,
      submissionNumber,
      applyStudySaveSuccess,
      handleSourceOnlyResponse
    ]
  );

  return {
    indications,
    attributes,
    endpointOptions,
    sharedStudyConfirmation: pendingSharedStudyEdit?.confirmation ?? null,
    confirmingSharedStudyEdit,
    savingStudy,
    getEndpointOptions,
    getPossibleAttributes,
    expandIndication,
    editIndication,
    saveIndication,
    updateIndicationText,
    addEmptyStudy,
    editStudy,
    deleteStudyData,
    saveStudyData,
    updateStaleStatus,
    copyStaleIndications,
    rejectIdentical,
    copyIndicationFromPrev,
    deleteIndicationId,
    addEmptyIndication,
    confirmSharedStudyEdit,
    cancelSharedStudyEdit
  };
};

export default useIndicationAPIs;
